import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import pluralize from 'pluralize';

import AccountCardNullState from '../AccountCardNullState/AccountCardNullState';
import AccountCard from '../../../../../../services/cards/components/AccountCard';
import useBasename from '../../../../../../components/routing/useBasename';
import useAccountCardClone from '../../useAccountCardClone';
import Button from '../../../../../../components/global/Button';
import { ACCOUNT_CARD_ADDRESSES_MODAL } from '../AccountCardAddressesModal';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import { getOrderStatusLabel } from '../../../../../../util/account-cards';

const propTypes = {
  className: PropTypes.string,
  accountCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ),
  nullText: PropTypes.node.isRequired,
  nullWithButton: PropTypes.bool,
  canEditAccountCards: PropTypes.bool,
  canCloneAccountCards: PropTypes.bool,
  canShareAccountCards: PropTypes.bool,
  includeDates: PropTypes.bool,
  includeSentTo: PropTypes.bool,
  onShare: PropTypes.func,
};

const AccountCardList = ({
  className,
  accountCards = [],
  nullText,
  nullWithButton = true,
  canEditAccountCards,
  canCloneAccountCards,
  canShareAccountCards,
  includeDates,
  includeSentTo,
  onShare,
  onAccountCardCloned,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const openModal = useOpenModal();
  const basename = useBasename();
  const history = useHistory();

  const { onAccountCardClone, cloning } = useAccountCardClone({
    onSuccess: accountCard => {
      if (onAccountCardCloned) {
        onAccountCardCloned(accountCard);
        return;
      }

      history.push(`/app/${basename}/account/cards/create/${accountCard.id}`);
    },
  });

  return (
    <div className={classes}>
      {accountCards.length === 0 && (
        <AccountCardNullState text={nullText} withButton={nullWithButton} />
      )}

      {accountCards.length > 0 && (
        <div className="account-card-list">
          {accountCards.map(accountCard => {
            const firstIdentity =
              accountCard.roster_identity_linked_identity_addresses
                ?.records?.[0]?.roster_identity_linked_identity?.identity;

            let totalAddressesSentTo =
              (accountCard?.roster_identity_linked_identity_addresses?.total ??
                0) +
              (accountCard?.external_shipping_addresses?.total ?? 0) +
              (accountCard?.external_shipping_address ? 1 : 0);

            if (firstIdentity) {
              totalAddressesSentTo -= 1;
            }

            return (
              <div
                key={accountCard.id}
                className={classNames({
                  'account-card-list-item': true,
                  'account-card-list-item-loading': cloning,
                  'can-edit': canEditAccountCards,
                  'can-clone': canCloneAccountCards,
                })}
              >
                {includeDates && accountCard.updated_at && (
                  <p className="text-bold text-sm">
                    {format(parseISO(accountCard.updated_at), 'MMMM d, yyyy')}
                  </p>
                )}
                <AccountCard
                  onElementConfigurationChange={() => {}}
                  accountCard={accountCard}
                  animatable={false}
                  viewOnly
                  disablePhotoWatermarkOverlay
                />

                <div
                  className={classNames({
                    'account-card-list-item-actions': true,
                    'include-send-to':
                      includeSentTo &&
                      (firstIdentity || totalAddressesSentTo > 0),
                  })}
                >
                  {canCloneAccountCards && (
                    <Button
                      disabled={cloning}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        onAccountCardClone(accountCard);
                      }}
                    >
                      {cloning ? 'Loading...' : 'Reuse design'}
                    </Button>
                  )}

                  {canEditAccountCards && (
                    <Button
                      to={`/app/${basename}/account/cards/create/${accountCard.id}`}
                    >
                      Edit
                    </Button>
                  )}
                  {canShareAccountCards && (
                    <Button onClick={() => onShare({ accountCard })}>
                      Share
                    </Button>
                  )}
                </div>
                {includeSentTo &&
                  (firstIdentity || totalAddressesSentTo > 0) && (
                    <div>
                      <Button
                        btnStyle="reset"
                        className="send-to-container"
                        onClick={() => {
                          openModal(ACCOUNT_CARD_ADDRESSES_MODAL, {
                            accountCard,
                          });
                        }}
                      >
                        Sent to:{' '}
                        {accountCard.roster_identity_linked_identity_addresses
                          ?.records?.[0]?.name ? (
                          <b className="align-items-center ml-1 truncated-text sent-to-text">
                            {
                              accountCard
                                .roster_identity_linked_identity_addresses
                                .records[0].name
                            }
                            {totalAddressesSentTo > 1 && (
                              <Fragment>
                                {' '}
                                &amp; {totalAddressesSentTo - 1} more
                              </Fragment>
                            )}
                          </b>
                        ) : (
                          <b className="ml-1">
                            {totalAddressesSentTo}{' '}
                            {pluralize('address', totalAddressesSentTo)}
                          </b>
                        )}
                      </Button>
                      <div className="order-status-label">
                        {getOrderStatusLabel({ accountCard })}
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

AccountCardList.propTypes = propTypes;

export default AccountCardList;
